import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import {
  ArrowLeft,
  ExternalLink,
  Trash2,
  Power,
  Pencil,
  Globe,
  FileText,
  File as FileIcon,
  Loader2,
  Save,
  X,
  type LucideIcon,
} from 'lucide-react'
import { API_URL } from '../config'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface KnowledgeDetail {
  _id: string
  title: string
  content: string
  type: 'txt' | 'pdf' | 'url'
  source: string
  status: string
  tags: string[]
  isActive: boolean
  metadata: {
    wordCount?: number
    pageCount?: number
    fileSize?: number
    lastUpdated?: string
  }
  createdBy?: { name?: string; email?: string }
  createdAt: string
  updatedAt: string
}

const TYPE_ICON: Record<string, LucideIcon> = { url: Globe, txt: FileText, pdf: FileIcon }

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'

const formatBytes = (bytes?: number) => {
  if (!bytes) return null
  const units = ['B', 'KB', 'MB']
  let n = bytes
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

/** One metadata row. Keeps label/value alignment identical down the panel. */
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-4 py-2">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span className="text-right font-medium">{children}</span>
  </div>
)

const KnowledgeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  // ?edit=1 lets the list's Edit button land straight in edit mode, and makes
  // "edit this item" a shareable URL rather than a transient modal state.
  const [searchParams, setSearchParams] = useSearchParams()

  const [item, setItem] = useState<KnowledgeDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const [editing, setEditing] = useState(false)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftContent, setDraftContent] = useState('')
  const [draftTags, setDraftTags] = useState('')

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  })

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/knowledge/${id}`, authHeader())
        setItem(data)
        if (searchParams.get('edit') === '1') {
          setDraftTitle(data.title)
          setDraftContent(data.content)
          setDraftTags((data.tags ?? []).join(', '))
          setEditing(true)
        }
      } catch (error: any) {
        console.error('Error loading knowledge item:', error)
        toast.error(error.response?.data?.error || 'Could not load this item')
        navigate('/knowledge', { replace: true })
      } finally {
        setLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const clearEditParam = () => {
    if (searchParams.has('edit')) {
      searchParams.delete('edit')
      setSearchParams(searchParams, { replace: true })
    }
  }

  const stopEditing = () => {
    setEditing(false)
    clearEditParam()
  }

  const startEditing = () => {
    if (!item) return
    setDraftTitle(item.title)
    setDraftContent(item.content)
    setDraftTags(item.tags.join(', '))
    setEditing(true)
  }

  const save = async () => {
    if (!item) return
    if (!draftTitle.trim()) return toast.error('Enter a title')
    if (!draftContent.trim()) return toast.error('Content cannot be empty')

    setBusy(true)
    try {
      // Sent as plain text, matching how content is stored — this is the text
      // the retrieval index searches, so markup would only pollute it.
      const { data } = await axios.put(
        `${API_URL}/knowledge/${item._id}`,
        {
          title: draftTitle.trim(),
          content: draftContent,
          tags: draftTags.split(',').map((t) => t.trim()).filter(Boolean),
        },
        authHeader()
      )
      // The update response is not populated, so createdBy comes back as a raw
      // id. Keep the name already loaded, otherwise "Added by" disappears
      // after every save.
      const updated: KnowledgeDetail = data.knowledge ?? {
        ...item,
        title: draftTitle.trim(),
        content: draftContent,
      }
      setItem({ ...updated, createdBy: item.createdBy ?? updated.createdBy })
      stopEditing()
      toast.success('Saved')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Could not save changes')
    } finally {
      setBusy(false)
    }
  }

  const toggleActive = async () => {
    if (!item) return
    setBusy(true)
    try {
      await axios.put(`${API_URL}/knowledge/${item._id}`, { isActive: !item.isActive }, authHeader())
      setItem({ ...item, isActive: !item.isActive, status: !item.isActive ? 'active' : 'inactive' })
      toast.success(item.isActive ? 'Deactivated' : 'Activated')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Could not update this item')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!item) return
    if (!confirm(`Delete "${item.title}"? This cannot be undone.`)) return
    setBusy(true)
    try {
      await axios.delete(`${API_URL}/knowledge/${item._id}`, authHeader())
      toast.success('Deleted')
      navigate('/knowledge')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Could not delete this item')
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-2/3" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-72 lg:col-span-1" />
          <Skeleton className="h-72 lg:col-span-2" />
        </div>
      </div>
    )
  }

  if (!item) return null

  const TypeIcon = TYPE_ICON[item.type] ?? FileText
  const isUrl = item.type === 'url' && /^https?:\/\//i.test(item.source)
  const fileSize = formatBytes(item.metadata?.fileSize)
  const words = item.content ? item.content.trim().split(/\s+/).filter(Boolean).length : 0

  return (
    <div className="space-y-6 pb-10">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/knowledge">
            <ArrowLeft />
            Back to knowledge
          </Link>
        </Button>

        <div className="flex flex-wrap items-center gap-2">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={stopEditing} disabled={busy}>
                <X />
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={busy}>
                {busy ? <Loader2 className="animate-spin" /> : <Save />}
                Save changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={startEditing} disabled={busy}>
                <Pencil />
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={toggleActive} disabled={busy}>
                {busy ? <Loader2 className="animate-spin" /> : <Power />}
                {item.isActive ? 'Deactivate' : 'Activate'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={remove}
                disabled={busy}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Title */}
      {editing ? (
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            className="h-11 text-base font-medium"
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold leading-tight tracking-tight">{item.title}</h1>
            <Badge variant={item.isActive ? 'success' : 'muted'}>
              {item.isActive ? 'Active' : 'Deactivated'}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {item.isActive
              ? 'The chatbot can use this when answering questions.'
              : 'Deactivated — the chatbot will not use this content.'}
          </p>
        </div>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-3">
        {/* Metadata. Sticky rather than scrollable: the details stay in view
            while reading long content, without trapping a scrollbar inside a
            panel. */}
        <Card className="lg:sticky lg:top-6 lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Details</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-border text-sm">
            <Row label="Type">
              <span className="inline-flex items-center gap-1.5">
                <TypeIcon className="h-4 w-4 text-muted-foreground" />
                {item.type.toUpperCase()}
              </span>
            </Row>

            <div className="space-y-1.5 py-2">
              <span className="text-muted-foreground">Source</span>
              {isUrl ? (
                <a
                  href={item.source}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-1.5 break-all font-medium text-primary underline-offset-4 hover:underline"
                >
                  {item.source}
                  <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                </a>
              ) : (
                <p className="break-all font-medium">{item.source || '—'}</p>
              )}
            </div>

            <Row label="Words">
              <span className="tabular-nums">
                {(editing ? words : item.metadata?.wordCount ?? words).toLocaleString()}
              </span>
            </Row>

            {item.metadata?.pageCount != null && (
              <Row label="Pages">
                <span className="tabular-nums">{item.metadata.pageCount}</span>
              </Row>
            )}

            {fileSize && <Row label="File size">{fileSize}</Row>}

            <Row label="Added">{formatDate(item.createdAt)}</Row>
            <Row label="Last edited">{formatDate(item.updatedAt)}</Row>

            {item.metadata?.lastUpdated && (
              <Row label="Content refreshed">{formatDate(item.metadata.lastUpdated)}</Row>
            )}

            {item.createdBy?.name && <Row label="Added by">{item.createdBy.name}</Row>}

            <div className="space-y-2 pt-3">
              <span className="text-muted-foreground">Tags</span>
              {editing ? (
                <>
                  <Input
                    value={draftTags}
                    onChange={(e) => setDraftTags(e.target.value)}
                    placeholder="comma, separated, tags"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tags help the chatbot find relevant information.
                  </p>
                </>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {item.tags?.length ? (
                    item.tags.map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted-foreground">None</span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Content. Flows down the page rather than scrolling inside a fixed
            box — a nested scrollbar makes long articles awkward to read. */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm">Content</CardTitle>
            <span className="text-xs text-muted-foreground">
              {editing ? 'Plain text — this is what the chatbot searches.' : `${words.toLocaleString()} words`}
            </span>
          </CardHeader>
          <CardContent>
            {editing ? (
              <Textarea
                value={draftContent}
                onChange={(e) => setDraftContent(e.target.value)}
                className="min-h-[28rem] font-mono text-sm leading-relaxed"
                spellCheck={false}
              />
            ) : (
              <div className="whitespace-pre-wrap break-words rounded-md bg-muted/40 p-5 text-sm leading-7">
                {item.content || 'No content stored for this item.'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default KnowledgeDetails
